import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { OpportunityStake } from '@tennis-scanner/shared';

// Compact stake chip for the OpportunityCard pick row. Primary number
// only on the surface; full Kelly breakdown lives in the tooltip.
//
// Manual takes precedence visually (operator's conscious size at pick
// time); Kelly is the fallback. Returns null when neither is available
// — caller renders nothing in that case.
export function StakeChip({
  stake,
  manualStakeUsd,
}: {
  stake: OpportunityStake | null;
  manualStakeUsd: number | null | undefined;
}) {
  const hasManual = typeof manualStakeUsd === 'number' && manualStakeUsd > 0;
  const hasKelly = stake !== null && stake.suggestedStakeUsd > 0;
  if (!hasManual && !hasKelly) return null;

  const primary = hasManual ? (manualStakeUsd as number) : stake!.suggestedStakeUsd;
  const label = hasManual ? 'pick' : 'kelly';
  const colorClass = hasManual
    ? 'text-info border-info/30 bg-info/5'
    : 'text-positive border-positive/30 bg-positive/5';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-baseline gap-1 rounded-md border px-1.5 py-0 font-mono leading-none',
            colorClass,
          )}
        >
          <span className="text-[9px] uppercase tracking-wider opacity-70">{label}</span>
          <span className="text-sm font-bold">${Math.round(primary).toLocaleString('en-US')}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs space-y-0.5">
        {hasManual && (
          <div>
            <span className="font-mono">
              stake del pick: ${(manualStakeUsd as number).toFixed(2)}
            </span>
          </div>
        )}
        {hasKelly && (
          <>
            <div>
              <span className="font-mono">
                Kelly: ${stake!.suggestedStakeUsd.toFixed(2)} (
                {(stake!.kellyPctApplied * 100).toFixed(2)}% bankroll)
              </span>
            </div>
            <div className="text-muted-foreground">
              fracción ×{stake!.kellyFractionUsed} · bankroll $
              {stake!.bankrollUsd.toLocaleString('en-US')}
              {stake!.inDrawdown && ' · drawdown activo'}
            </div>
          </>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
